// AdminNotificationsStats.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./AdminNotificationsStats.module.scss";
import { timeAgo } from "@/utils/timeAgo";

const TYPE_LABELS = {
  general: "General",
  new_user: "New User",
  membership_renewed: "Membership Renewed",
  new_member_profile: "New Member Profile",
  wallet_refill: "Wallet Refill",
  new_member_fee: "New Member Fee",
  club_join_request_received: "Club Join Request Received",
  club_join_request_accepted: "Club Join Request Accepted",
  club_join_request_rejected: "Club Join Request Rejected",
};

export default function AdminNotificationsStats() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/v1/admin/layout/unread-notifications");
      setItems(res.data?.items ?? []);
    } catch (err) {
      console.error("Failed to fetch unread notifications:", err);
      setError("Couldn't load notifications.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  const unreadCount = items.length;

  const countText = useMemo(() => {
    if (loading) return "…";
    if (unreadCount > 99) return "99+";
    return String(unreadCount);
  }, [loading, unreadCount]);

  const markRead = async (id) => {
    try {
      await api.patch(`/v1/admin/layout/notification/${id}/read`);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const openCta = async (n) => {
    // mark read, then navigate
    await markRead(n.id);
    if (n.ctaPath) navigate(n.ctaPath);
  };

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div className={cls.headLeft}>
          <h1 className={cls.title}>Notifications</h1>
          <p className={cls.subtitle}>
            You have <span className={cls.countPill}>{countText}</span> unread{" "}
            notification{unreadCount === 1 ? "" : "s"}.
          </p>
        </div>

        <Link to="/admin/notifications/all" className={cls.viewAllBtn}>
          <span>View all</span>
          <span className={cls.arrow} aria-hidden="true">
            →
          </span>
        </Link>
      </header>

      {error && <div className={cls.error}>{error}</div>}

      {loading ? (
        <div className={cls.skeletonGrid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div className={cls.skeletonCard} key={i} />
          ))}
        </div>
      ) : unreadCount === 0 ? (
        <div className={cls.empty}>
          <div className={cls.emptyCard}>
            <div className={cls.emptyIcon}>✓</div>
            <p className={cls.emptyTitle}>All caught up</p>
            <p className={cls.emptySub}>No unread notifications right now.</p>
          </div>
        </div>
      ) : (
        <div className={cls.grid}>
          {items.map((n) => {
            const label = TYPE_LABELS[n.type] || n.type;

            return (
              <article className={cls.card} key={n.id}>
                <div className={cls.cardTop}>
                  <span className={cls.meta}>
                    <span
                      className={`${cls.type} ${
                        cls[`type--${n.type}`] || cls["type--general"]
                      }`}
                      title={n.type}
                    >
                      {label}
                    </span>
                    <span className={cls.dot} aria-hidden="true">
                      •
                    </span>
                    <time className={cls.time}>{timeAgo(n.createdAt)}</time>
                  </span>

                  <button
                    type="button"
                    className={cls.readIconBtn}
                    onClick={() => markRead(n.id)}
                    aria-label="Mark as read"
                    title="Mark as read"
                  >
                    ✓
                  </button>
                </div>

                <h3 className={cls.cardTitle} title={n.title}>
                  {n.title}
                </h3>

                {n.description ? (
                  <p className={cls.desc}>{n.description}</p>
                ) : null}

                <div className={cls.actions}>
                  {n.ctaPath ? (
                    <button
                      type="button"
                      className={cls.ctaBtn}
                      onClick={() => openCta(n)}
                      aria-label={n.ctaLabel || "Open"}
                      title={n.ctaLabel || "Open"}
                    >
                      <span className={cls.ctaText}>
                        {n.ctaLabel || "Open"}
                      </span>
                      <span className={cls.ctaArrow} aria-hidden="true">
                        →
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={cls.softBtn}
                      onClick={() => markRead(n.id)}
                      aria-label="Mark as read"
                      title="Mark as read"
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
